import { Product } from '@labrav/common'
import { ProductType } from '@labrav/common'
import { parseProductPathSegments } from '../common/utils/parseProductPathSegments'

/**
 * The always-present half of the chat's page context: where the user is and who
 * they are. What is *on* the screen is contributed by the screens themselves via
 * `registerChatContext`, because only they know whether they address a record,
 * what its ids are called, or what is filtered.
 *
 * `path` and `query` are sent raw. A route-pattern table was the earlier design
 * and it silently dropped anything it did not recognise; the raw URL needs no
 * maintenance and covers every route, including ones nobody registered. Secret-
 * looking query keys are redacted by the shared sanitizer before this leaves the
 * browser.
 *
 * Unknown fields are omitted from the request rather than sent as null.
 */
export type PageCoreContext = {
  path: string
  query: Record<string, string> | null
  product: Product | null
  cloud: string | null
  accountId: string | null
  partnerId: string | null
  companyName: string | null
  userId: string | null
  roles: string[]
}

/**
 * Structural slice of the store this reads. Narrower than RootState on purpose:
 * it keeps this out of the store's import graph and lets tests pass fixtures.
 */
export type PageContextState = {
  AccountData?: {
    user?: {
      partnerData?: {
        partnerId?: string | null
        companyName?: string | null
      } | null
    }
  }
  userProfile?: {
    userProfile?: {
      id?: string | null
      roles?: string[]
    }
  }
}

export type PageLocation = {
  pathname: string
  search: string
}

const PRODUCT_SLUGS: Product[] = ['oppsync', 'flyout', 'cloudfaas', 'funding']

const PRODUCT_TYPE_SLUGS: ProductType[] = ['oppsync', 'flyout', 'cloudfaas']

const isProductType = (product: Product | null): product is ProductType =>
  product !== null && (PRODUCT_TYPE_SLUGS as string[]).includes(product)

const productFromPathname = (pathname: string): Product | null => {
  const [firstSegment] = pathname.replace(/^\/+/, '').split('/')
  return PRODUCT_SLUGS.find(slug => slug === firstSegment) ?? null
}

const queryFromSearch = (search: string): Record<string, string> | null => {
  const entries = [...new URLSearchParams(search).entries()]
  return entries.length ? Object.fromEntries(entries) : null
}

/**
 * Pure — pass state and location in, get the core block out. Cheap enough that
 * call sites use `useMemo` rather than the app taking on reselect.
 */
export const selectPageContext = (
  state: PageContextState,
  location: PageLocation
): PageCoreContext => {
  const { pathname, search } = location
  const product = productFromPathname(pathname)
  const segments = isProductType(product)
    ? parseProductPathSegments(pathname, product)
    : null
  const partnerData = state.AccountData?.user?.partnerData
  const userProfile = state.userProfile?.userProfile

  return {
    path: pathname,
    query: queryFromSearch(search),
    product,
    cloud: segments?.cloud ?? null,
    accountId: segments?.accountId ?? null,
    partnerId: partnerData?.partnerId ?? null,
    companyName: partnerData?.companyName ?? null,
    userId: userProfile?.id ?? null,
    roles: Array.isArray(userProfile?.roles) ? userProfile.roles : [],
  }
}
